import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (_err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check if we're processing a Google OAuth callback
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const handleAuthResponse = (data) => {
    // Fallback bearer token for cross-site environments where cookies may fail
    if (data?.session_token) {
      localStorage.setItem('session_token', data.session_token);
    }
    setUser(data.user);
    return data;
  };

  const signup = async ({ email, password, full_name, phone, terms_accepted, sms_consent }) => {
    const res = await api.post('/auth/signup', {
      email, password, full_name, phone, terms_accepted, sms_consent, origin_url: window.location.origin,
    });
    return handleAuthResponse(res.data);
  };

  const login = async ({ email, password }) => {
    const res = await api.post('/auth/login', { email, password });
    return handleAuthResponse(res.data);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const res = await api.post('/auth/google/session', null, {
      headers: { 'X-Session-ID': sessionId },
    });
    return handleAuthResponse(res.data);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (_e) { /* ignore */ }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const res = await api.post('/auth/forgot-password', { email, origin_url: window.location.origin });
    return res.data;
  };

  const verifyEmail = async (token) => {
    const res = await api.get('/auth/verify-email', { params: { token } });
    return res.data;
  };

  const resetPassword = async (token, new_password) => {
    const res = await api.post('/auth/reset-password', { token, new_password });
    return res.data;
  };

  const updateProfile = async (payload) => {
    const res = await api.patch('/auth/me', payload);
    setUser(res.data);
    return res.data;
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    return roles.some((r) => user.roles?.includes(r));
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading,
        signup, login, googleLogin, exchangeGoogleSession, logout,
        forgotPassword, resetPassword, verifyEmail, updateProfile, checkAuth, hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
